/**
 * Typed convenience wrappers around the raw fetch client. Keeps URL paths and
 * shape transformations in one place so callers (stores / pages / tests) can
 * import strongly-typed functions.
 */
import { api, apiRequest } from './client';
import type {
  CreateDriverInput,
  CreateVehicleInput,
  CursorPage,
  DriverDetail,
  DriverListItem,
  DriverTransitionInput,
  EligibilityCheckResult,
  FuelReceiptListItem,
  LoginRequest,
  LoginResponse,
  MeResponse,
  OnboardingRequirementsResponse,
  RefreshResponse,
  ThemeMode,
  UpdateDriverInput,
  UpdateVehicleInput,
  VehicleDetail,
  VehicleListItem,
  VehicleTransitionTarget,
  AiJobView,
  AuditLogEntry,
  CockpitResponse,
  CreateStationInput,
  CreateWorkshopTaskInput,
  DocumentListItem,
  UploadDocumentInput,
  DamageDetail,
  DamageListItem,
  DamagePhoto,
  AccommodationDetail,
  AccommodationListItem,
  CreatePayrollRunInput,
  ExportPayrollInput,
  KpiMetric,
  KpiResult,
  KpiSnapshot,
  PayrollExportFile,
  PayrollRunDetail,
  PayrollRunListItem,
  ReportingExportFile,
  AssignOccupancyInput,
  AssignSimInput,
  CreateAccommodationInput,
  CreateSimInput,
  FuelReceiptDetail,
  OccupancyDetailRow,
  OccupancyListItem,
  ReleaseOccupancyInput,
  SimAssignmentRow,
  SimDetail,
  SimListItem,
  Station,
  StationAssignment,
  TicketDetail,
  UpdateStationInput,
  TicketListItem,
  WorkshopTaskDetail,
  WorkshopTaskListItem,
  WorkshopTaskTransitionTarget,
} from './types';

export const AuthApi = {
  login: (input: LoginRequest) =>
    api.post<LoginResponse>('/auth/login', input, /* anonymous */ true),
  refresh: (refresh_token: string) =>
    api.post<RefreshResponse>('/auth/refresh', { refresh_token }, true),
  logout: (refresh_token: string | null) =>
    api.post<void>('/auth/logout', { refresh_token }),
  me: () => api.get<MeResponse>('/auth/me'),
};

export const UsersApi = {
  /**
   * Backend endpoint is being built in parallel — it may not exist yet (404)
   * during early Phase 5A development. Callers handle that case (fire-and-forget).
   */
  updateMe: (patch: { theme_preference?: ThemeMode; locale?: string }) =>
    api.patch<{ id: string; email: string; theme_preference: string; locale: string }>(
      '/users/me',
      patch,
    ),
};

export const DriversApi = {
  list: (query?: {
    status?: string;
    q?: string;
    personal_id?: string;
    home_station_id?: string;
    limit?: number;
    cursor?: string;
  }) => api.get<CursorPage<DriverListItem>>('/drivers', query),

  getById: (id: string) => api.get<DriverDetail>(`/drivers/${id}`),

  /** POST /drivers — backend requires permission `driver.create`. */
  create: (input: CreateDriverInput) => api.post<DriverDetail>('/drivers', input),

  /**
   * PATCH /drivers/:id — backend requires permission `driver.write`. Only
   * fields on UpdateDriverInput are accepted; everything else (status,
   * personal_id, service-managed timestamps) is rejected by the global
   * ValidationPipe.
   */
  update: (id: string, patch: UpdateDriverInput) =>
    api.patch<DriverDetail>(`/drivers/${id}`, patch),

  /**
   * DELETE /drivers/:id — admin-only hard-delete (D-038 atomic terminate +
   * archive(force=true)). Backend requires `driver.archive`. Returns 204.
   */
  delete: (id: string) => api.delete<void>(`/drivers/${id}`),

  /**
   * POST /drivers/:id/transitions — generic lifecycle transition. The backend
   * decides per-target which permission is required; UI buttons gate visibility
   * via {@link usePermission}.
   */
  transition: (id: string, input: DriverTransitionInput) =>
    api.post<DriverDetail>(`/drivers/${id}/transitions`, input),

  /** suspend = transition to suspended; reason required. */
  suspend: (id: string, reason: string) =>
    api.post<DriverDetail>(`/drivers/${id}/transitions`, { to: 'suspended', reason }),

  /** archive = transition to archived; reason optional. */
  archive: (id: string, reason: string) =>
    api.post<DriverDetail>(`/drivers/${id}/transitions`, { to: 'archived', reason }),

  /** activate = transition to active (used for onboarding->active or suspended->active reactivation). */
  activate: (id: string, reason?: string) =>
    api.post<DriverDetail>(`/drivers/${id}/transitions`, { to: 'active', ...(reason ? { reason } : {}) }),

  /** terminate = transition to terminated; reason required. */
  terminate: (id: string, reason: string) =>
    api.post<DriverDetail>(`/drivers/${id}/transitions`, { to: 'terminated', reason }),

  /** Reset to onboarding (e.g. lead -> onboarding). */
  setOnboarding: (id: string, reason?: string) =>
    api.post<DriverDetail>(`/drivers/${id}/transitions`, { to: 'onboarding', ...(reason ? { reason } : {}) }),
};

export const OnboardingApi = {
  /**
   * GET /onboarding/requirements?worker_type=driver|external_worker.
   * Returns the tenant-effective activation rule snapshot.
   * D-170 / D-171: accepts onboarding.read OR driver.read OR driver.create.
   */
  getRequirements: (workerType: 'driver' | 'external_worker' = 'driver') =>
    api.get<OnboardingRequirementsResponse>('/onboarding/requirements', { worker_type: workerType }),

  /**
   * GET /onboarding/leads/:id/eligibility — Single Source of Truth (D-170c).
   * Returns the structured met / missing checks the UI renders as
   * Eligibility-Block on the DriverDetailPage.
   */
  getEligibility: (driverId: string) =>
    api.get<EligibilityCheckResult>(`/onboarding/leads/${driverId}/eligibility`),

  /**
   * POST /onboarding/leads/:id/complete — activate driver via the onboarding
   * workflow (server runs the eligibility check internally and returns
   * 422 `eligibility_failed` if missing requirements remain).
   */
  completeOnboarding: (driverId: string) =>
    api.post<DriverDetail>(`/onboarding/leads/${driverId}/complete`),
};

export const FleetApi = {
  list: (query?: { limit?: number; status?: string; q?: string; cursor?: string; assigned_driver_id?: string; station_id?: string; service_due?: 'true' | 'false'; sort?: string }) =>
    api.get<CursorPage<VehicleListItem>>('/vehicles', query),
  getById: (id: string) => api.get<VehicleDetail>(`/vehicles/${id}`),
  create: (input: CreateVehicleInput) => api.post<VehicleDetail>('/vehicles', input),
  update: (id: string, patch: UpdateVehicleInput) =>
    api.patch<VehicleDetail>(`/vehicles/${id}`, patch),
  transition: (id: string, to: VehicleTransitionTarget, reason?: string) =>
    api.post<VehicleDetail>(`/vehicles/${id}/transitions`, { to, ...(reason ? { reason } : {}) }),
  assignDriver: (id: string, driverId: string, notes?: string) =>
    api.post<VehicleDetail>(`/vehicles/${id}/assignments`, { driver_id: driverId, notes }),
  unassign: (id: string, reason?: string) =>
    api.delete<void>(`/vehicles/${id}/assignments` + (reason ? `?reason=${encodeURIComponent(reason)}` : '')),
};

export const WorkshopApi = {
  list: (query?: { limit?: number; status?: string; vehicle_id?: string; q?: string; cursor?: string }) =>
    api.get<CursorPage<WorkshopTaskListItem>>('/workshop-tasks', query),
  getById: (id: string) => api.get<{ task: WorkshopTaskDetail }>(`/workshop-tasks/${id}`),
  create: (input: CreateWorkshopTaskInput) => api.post<{ task: WorkshopTaskDetail }>('/workshop-tasks', input),
  transition: (id: string, to: WorkshopTaskTransitionTarget, reason?: string, extras?: { cost_cents?: number; actual_hours?: number }) =>
    api.post<{ task: WorkshopTaskDetail }>(`/workshop-tasks/${id}/transitions`, {
      to_status: to,
      ...(reason ? { reason } : {}),
      ...(extras?.cost_cents != null ? { cost_cents: extras.cost_cents } : {}),
      ...(extras?.actual_hours != null ? { actual_hours: extras.actual_hours } : {}),
    }),
  assign: (id: string, assignedToUserId: string) =>
    api.post<{ task: WorkshopTaskDetail }>(`/workshop-tasks/${id}/assign`, { assigned_workshop_user_id: assignedToUserId }),
};

export const DamageApi = {
  list: (query?: { limit?: number; status?: string; severity?: string; vehicle_id?: string; q?: string; cursor?: string }) =>
    api.get<CursorPage<DamageListItem>>('/damages', query),
  getById: (id: string) => api.get<{ damage: DamageDetail; photos: DamagePhoto[]; latest_ai_job: AiJobView | null }>(`/damages/${id}`),
  transition: (
    id: string,
    to: 'triage' | 'in_workshop' | 'resolved' | 'wontfix',
    opts?: { reason?: string; actual_cost_cents?: number },
  ) =>
    api.post<{ damage: DamageDetail }>(`/damages/${id}/transitions`, {
      to,
      ...(opts?.reason ? { reason: opts.reason } : {}),
      ...(opts?.actual_cost_cents != null ? { actual_cost_cents: opts.actual_cost_cents } : {}),
    }),
  triggerAiAnalysis: (id: string, photoId?: string) =>
    api.post<{ ai_job_id: string }>(`/damages/${id}/ai-analyze`, photoId ? { photo_id: photoId } : {}),
  getAiJob: (id: string, jobId: string) =>
    api.get<AiJobView>(`/damages/${id}/ai-jobs/${jobId}`),
};

export const DocumentsApi = {
  list: (query?: { limit?: number; document_type?: string; related_driver_id?: string; related_vehicle_id?: string; related_accommodation_id?: string; status?: string; cursor?: string }) =>
    api.get<CursorPage<DocumentListItem>>('/documents', query),
  getById: (id: string) => api.get<DocumentListItem>(`/documents/${id}`),
  upload: (input: UploadDocumentInput) => api.post<DocumentListItem>('/documents', input),
  remove: (id: string, reason?: string) =>
    api.delete<void>(`/documents/${id}` + (reason ? `?reason=${encodeURIComponent(reason)}` : '')),
};

export const DispatcherApi = {
  cockpit: () => api.get<CockpitResponse>('/dispatcher/cockpit'),
  conflicts: () => api.get<{ items: Array<Record<string, unknown>> }>('/dispatcher/conflicts'),
  reassignDriver: (input: { attendance_id: string; from_driver_id: string; to_driver_id: string; reason: string }) =>
    api.post<void>('/dispatcher/reassign-driver', input),
  reassignShuttle: (input: { shuttle_id: string; from_driver_id: string; to_driver_id: string; reason: string }) =>
    api.post<void>('/dispatcher/reassign-shuttle', input),
  resolveConflict: (attendanceId: string, input: { resolution: string; reason: string }) =>
    api.post<void>(`/dispatcher/conflicts/${attendanceId}/resolve`, input),
};

export const TicketsApi = {
  list: (query?: { limit?: number; status?: string; severity?: string; source_module?: string; q?: string; cursor?: string }) =>
    api.get<CursorPage<TicketListItem>>('/tickets', query),
  getById: (id: string) => api.get<TicketDetail>(`/tickets/${id}`),
  assign: (id: string, assigneeUserId: string) =>
    api.post<TicketDetail>(`/tickets/${id}/assign`, { assignee_user_id: assigneeUserId }),
  escalate: (id: string, reason: string) =>
    api.post<TicketDetail>(`/tickets/${id}/escalate`, { reason }),
  /** Backend uses generic transitions endpoint with to_status='closed' + resolution. */
  close: (id: string, resolution: 'resolved' | 'duplicate' | 'wontfix' | 'invalid', reason?: string) =>
    api.post<TicketDetail>(`/tickets/${id}/transitions`, {
      to_status: 'closed',
      resolution,
      ...(reason ? { reason } : {}),
    }),
  /** Generic transition (in_progress, waiting, resolved). */
  transition: (
    id: string,
    to: 'in_progress' | 'waiting' | 'resolved' | 'closed' | 'escalated',
    opts?: { reason?: string; resolution?: 'resolved' | 'duplicate' | 'wontfix' | 'invalid' },
  ) =>
    api.post<TicketDetail>(`/tickets/${id}/transitions`, {
      to_status: to,
      ...(opts?.reason ? { reason: opts.reason } : {}),
      ...(opts?.resolution ? { resolution: opts.resolution } : {}),
    }),
};

export const FuelApi = {
  list: (query?: { limit?: number; status?: string; driver_id?: string; vehicle_id?: string; cursor?: string }) =>
    api.get<CursorPage<FuelReceiptListItem>>('/fuel-receipts', query),
  getById: (id: string) => api.get<FuelReceiptDetail>(`/fuel-receipts/${id}`),
  validate: (id: string) => api.post<FuelReceiptDetail>(`/fuel-receipts/${id}/validate`),
  dispute: (id: string, reason: string) =>
    api.post<FuelReceiptDetail>(`/fuel-receipts/${id}/dispute`, { reason }),
  reparse: (id: string) => api.post<FuelReceiptDetail>(`/fuel-receipts/${id}/parse`),
};

export const SimsApi = {
  list: (query?: { limit?: number; cursor?: string; status?: string; provider?: string; assigned_driver_id?: string; q?: string }) =>
    api.get<CursorPage<SimListItem>>('/sims', query),
  getById: (id: string) => api.get<SimDetail>(`/sims/${id}`),
  create: (input: CreateSimInput) => api.post<SimListItem>('/sims', input),
  assign: (simId: string, input: AssignSimInput) =>
    api.post<SimAssignmentRow>(`/sims/${simId}/assignments`, input),
  unassign: (simId: string, assignmentId: string, reason?: string) =>
    apiRequest<SimAssignmentRow>({
      method: 'DELETE',
      path: `/sims/${simId}/assignments/${assignmentId}`,
      body: reason ? { reason } : undefined,
    }),
  reportLost: (simId: string, reason: string, lastSeen?: string) =>
    api.post<SimListItem>(`/sims/${simId}/report-lost`, { reason, last_seen_location: lastSeen }),
  transition: (simId: string, to: 'suspended' | 'free' | 'terminated', reason?: string) =>
    api.post<SimListItem>(`/sims/${simId}/transitions`, { to, reason }),
};

export const AccommodationApi = {
  occupancyByDriver: (driverId: string) =>
    api.get<OccupancyDetailRow | { occupancy: null }>(`/accommodations/occupancy/by-driver/${driverId}`),
  list: (query?: { limit?: number; cursor?: string; q?: string; city?: string; is_active?: 'true' | 'false'; sort?: string }) =>
    api.get<CursorPage<AccommodationListItem>>('/accommodations', query),
  getById: (id: string) => api.get<AccommodationDetail>(`/accommodations/${id}`),
  create: (input: CreateAccommodationInput) => api.post<AccommodationListItem>('/accommodations', input),
  deactivate: (id: string) => api.delete<void>(`/accommodations/${id}`),
  listOccupancies: (accId: string, onlyActive = true) =>
    api.get<OccupancyListItem[]>(`/accommodations/${accId}/occupancies?active=${onlyActive ? 'true' : 'false'}`),
  assignOccupancy: (accId: string, input: AssignOccupancyInput) =>
    api.post<OccupancyListItem>(`/accommodations/${accId}/occupancies`, input),
  releaseOccupancy: (accId: string, occId: string, input: ReleaseOccupancyInput) =>
    apiRequest<OccupancyListItem>({
      method: 'DELETE',
      path: `/accommodations/${accId}/occupancies/${occId}`,
      body: input,
    }),
  startCleaning: (accId: string, roomId: string, reason?: string, force?: boolean) =>
    api.post<void>(`/accommodations/${accId}/rooms/${roomId}/cleaning/start`, { reason, force }),
  completeCleaning: (accId: string, roomId: string, notes?: string) =>
    api.post<void>(`/accommodations/${accId}/rooms/${roomId}/cleaning/complete`, { notes }),
};

export const StationsApi = {
  list: (query?: { limit?: number; cursor?: string; type?: string; is_active?: 'true' | 'false'; q?: string }) =>
    api.get<CursorPage<Station>>('/stations', query),
  getById: (id: string) => api.get<Station>(`/stations/${id}`),
  create: (input: CreateStationInput) => api.post<Station>('/stations', input),
  update: (id: string, input: UpdateStationInput) => api.patch<Station>(`/stations/${id}`, input),
  deactivate: (id: string) => api.delete<void>(`/stations/${id}`),
  listAssignments: (id: string) =>
    api.get<{ data: StationAssignment[] }>(`/stations/${id}/dispatchers`),
  assignDispatcher: (id: string, input: { user_id: string; role?: 'dispatcher' | 'station_lead' }) =>
    api.post<StationAssignment>(`/stations/${id}/dispatchers`, input),
  revokeDispatcher: (id: string, userId: string) =>
    api.delete<void>(`/stations/${id}/dispatchers/${userId}`),
};

export const ReportingApi = {
  kpisLive: (query: { from: string; to: string; metrics: KpiMetric[]; group_by?: 'station' | 'vehicle' | 'tenant' }) =>
    api.get<{ items: KpiResult[] } | KpiResult[]>('/reporting/kpis', {
      from: query.from,
      to: query.to,
      metrics: query.metrics.join(','),
      ...(query.group_by ? { group_by: query.group_by } : {}),
    }),
  snapshots: (query?: { metric?: KpiMetric; from?: string; to?: string; period_type?: 'daily' | 'monthly' }) =>
    api.get<{ items: KpiSnapshot[] }>('/reporting/snapshots', query),
  runSnapshot: (date?: string) => api.post<{ written: number }>('/reporting/snapshots/run', date ? { date } : {}),
  dashboard: (role: string) => api.get<Record<string, unknown>>(`/reporting/dashboards/${role}`),
  createExport: (input: { format: 'csv' | 'json'; metrics: KpiMetric[]; from: string; to: string }) =>
    api.post<ReportingExportFile>('/reporting/exports', input),
  downloadExport: (fileId: string) => `/api/v1/reporting/exports/${fileId}`,
};

export const PayrollApi = {
  list: (query?: { limit?: number; cursor?: string; year?: number; month?: number; status?: string; station_id?: string }) =>
    api.get<CursorPage<PayrollRunListItem>>('/payroll-runs', query),
  getById: (id: string) => api.get<PayrollRunDetail>(`/payroll-runs/${id}`),
  create: (input: CreatePayrollRunInput) => api.post<PayrollRunListItem>('/payroll-runs', input),
  calculate: (id: string) => api.post<PayrollRunDetail>(`/payroll-runs/${id}/calculate`),
  lock: (id: string) => api.post<PayrollRunListItem>(`/payroll-runs/${id}/lock`),
  export: (id: string, input: ExportPayrollInput) => api.post<PayrollExportFile>(`/payroll-runs/${id}/export`, input),
  download: (id: string, fileId: string) => `/api/v1/payroll-runs/${id}/export/${fileId}`,
};

export const AuditApi = {
  list: (query?: {
    limit?: number;
    cursor?: string;
    action?: string;
    resource_type?: string;
    resource_id?: string;
    actor_user_id?: string;
    result?: 'success' | 'denied' | 'error';
    cross_tenant?: 'true' | 'false';
    from?: string;
    to?: string;
  }) => api.get<CursorPage<AuditLogEntry>>('/audit', query),
  getById: (id: string) => api.get<AuditLogEntry>(`/audit/${id}`),
};
